/**
 * De opzet van een historisch verhaal (stap 17), om te lezen: tijd en plaats,
 * het plotsjabloon, het vertelperspectief, de actanten van Greimas per as,
 * het vertelplan (stap 17c: hoofdpersoon, gebrek, inzet en klok, het geweer
 * van Tsjechov, wat mislukt, de confrontatie), de deelteksten met hun
 * plotstap en vorm, de bouwstenen, de modelreconstructie in zeven zinnen, de
 * variant voor een groepje van vier, de causale eindvraag, de historische
 * verantwoording en wat de nakijkronde vond. Sinds stap 17d ook de karakters,
 * naar welk verhaal dit een tweede versie is, de vergelijking met dat
 * verhaal per bouwsteen, en de andere versies (`verwant`, van de server).
 * Voor historische fictie en fictie van nu (`genre`).
 *
 * Voor de beheerder (bij het schrijven en bewerken) en de leerkracht (bij
 * een tekst); de leerling ziet dit niet. De opzet komt van de server
 * (texts.opzet, services/historische_fictie.py).
 */
import React from 'react';

// Dezelfde volgorde en namen als ACTANTEN in services/historische_fictie.py.
export const ACTANTEN = [
  { sleutel: 'subject', naam: 'Subject', as: 'verlangen' },
  { sleutel: 'object', naam: 'Object', as: 'verlangen' },
  { sleutel: 'zender', naam: 'Zender', as: 'overdracht' },
  { sleutel: 'ontvanger', naam: 'Ontvanger', as: 'overdracht' },
  { sleutel: 'helper', naam: 'Helper', as: 'macht' },
  { sleutel: 'tegenstander', naam: 'Tegenstander', as: 'macht' },
];

export const PERSPECTIEVEN = { ik: 'Ik-verteller', derde: 'Derde persoon' };

// De velden van het vertelplan, in de volgorde van de prompt (PLANVELDEN in services/historische_fictie.py).
const PLAN = [
  ['hoofdpersoon', 'Hoofdpersoon'], ['gebrek', 'Gebrek'], ['inzet', 'Wat er op het spel staat'], ['klok', 'De klok'],
  ['mislukking', 'Wat mislukt'], ['dichte_deur', 'Dichte deur'], ['risico', 'Risico'], ['confrontatie', 'Confrontatie'],
];

const kop = { fontSize: 15, margin: '16px 0 8px' };
const lijst = { margin: '0 0 0 20px', lineHeight: 1.6 };

export function Verhaalopzet({ opzet, genre, verwant, toonNakijkronde = true }) {
  if (!opzet) return null;
  const vanNu = genre === 'fictie_van_nu';
  const karakters = opzet.karakters || [];
  const parallel = opzet.parallel || [];
  const actanten = opzet.actanten || {};
  const verantwoording = opzet.verantwoording || {};
  const plan = opzet.plan || {};
  const geweer = plan.geweer || {};
  const scenes = plan.scenes || [];
  return (
    <div style={{ fontSize: 14 }} data-testid="verhaalopzet">
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {opzet.tijdvak ? <span><strong>Tijdvak:</strong> {opzet.tijdvak}</span> : vanNu && <span><strong>Tijd:</strong> nu</span>}
        {opzet.plaats && <span><strong>Plaats:</strong> {opzet.plaats}</span>}
        {opzet.situatie && <span><strong>Situatie:</strong> {opzet.situatie}</span>}
        {opzet.perspectief && <span><strong>Vertelperspectief:</strong> {PERSPECTIEVEN[opzet.perspectief] || opzet.perspectief}</span>}
      </div>
      {opzet.sjabloon_naam && (
        <p className="mt-8"><strong>Plotsjabloon:</strong> {opzet.sjabloon}. {opzet.sjabloon_naam}
          {opzet.focus && <span className="text-muted"> — {opzet.focus}</span>}</p>
      )}
      {(opzet.themawoorden || []).length > 0 && (
        <p><strong>Themawoorden:</strong> {opzet.themawoorden.join(', ')}</p>
      )}
      {opzet.voorbeeld?.titel && (
        <p data-testid="voorbeeld"><strong>Tweede versie van:</strong> {opzet.voorbeeld.titel}
          {opzet.voorbeeld.tijd && <span className="text-muted"> (speelt {opzet.voorbeeld.tijd})</span>}
          {verwant && !verwant.some(v => v.id === opzet.voorbeeld.id) && (
            <span style={{ color: 'var(--waarschuw-tekst)' }}> — dat verhaal is er niet meer</span>
          )}
          {(verwant || []).some(v => v.id === opzet.voorbeeld.id && v.verouderd) && (
            <span style={{ color: 'var(--waarschuw-tekst)' }}> — het voorbeeld is sindsdien veranderd; de vergelijking per
              bouwsteen klopt misschien niet meer</span>
          )}
        </p>
      )}
      {(verwant || []).length > 0 && (
        <p data-testid="verwant"><strong>Andere versies:</strong>{' '}
          {verwant.map((v, i) => (
            <span key={v.id}>{i > 0 && '; '}{v.titel} <span className="text-muted">
              ({{ voorbeeld: 'het voorbeeld', versie: 'een versie hiervan', familie: 'hetzelfde verhaal' }[v.rol] || v.rol},
              speelt {v.tijd}{v.rol === 'versie' && v.verouderd ? '; geschreven naar een eerdere tekst van dit verhaal' : ''})
            </span></span>
          ))}
        </p>
      )}
      {karakters.length > 0 && (
        <div data-testid="karakters-opzet">
          <h4 style={kop}>Karakters</h4>
          <ul style={{ ...lijst, listStyle: 'none', marginLeft: 0 }}>
            {karakters.map((k, i) => (
              <li key={i}><strong>{k.naam}</strong> <span className="text-muted">({k.rol})</span>{k.beschrijving && `: ${k.beschrijving}`}</li>
            ))}
          </ul>
        </div>
      )}

      <h4 style={kop}>Actanten (Greimas)</h4>
      <table style={{ fontSize: 13 }}>
        <thead><tr><th>As</th><th>Actant</th><th>In dit verhaal</th><th>Propp</th></tr></thead>
        <tbody>
          {ACTANTEN.map(a => (
            <tr key={a.sleutel}>
              <td className="text-muted">{a.as}</td>
              <td>{a.naam}</td>
              <td>{actanten[a.sleutel]?.invulling || '—'}</td>
              <td className="text-muted">{actanten[a.sleutel]?.propp || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {opzet.sleutelvoorwerp && <p className="mt-8"><strong>Sleutelvoorwerp:</strong> {opzet.sleutelvoorwerp}</p>}

      {(PLAN.some(([v]) => plan[v]) || geweer.opzet || geweer.uitbetaling) && (
        <div data-testid="vertelplan">
          <h4 style={kop}>Vertelplan</h4>
          <ul style={{ ...lijst, listStyle: 'none', marginLeft: 0 }}>
            {PLAN.slice(0, 4).filter(([v]) => plan[v]).map(([v, naam]) => <li key={v}><strong>{naam}:</strong> {plan[v]}</li>)}
            {(geweer.opzet || geweer.uitbetaling) && (
              <li>
                <strong>Het geweer (opzet en uitbetaling):</strong> {geweer.opzet}
                {geweer.opzet_bouwsteen && <code> [{geweer.opzet_bouwsteen}]</code>} → {geweer.uitbetaling}
                {geweer.uitbetaling_bouwsteen && <code> [{geweer.uitbetaling_bouwsteen}]</code>}
              </li>
            )}
            {PLAN.slice(4).filter(([v]) => plan[v]).map(([v, naam]) => <li key={v}><strong>{naam}:</strong> {plan[v]}</li>)}
          </ul>
        </div>
      )}

      {(opzet.delen || []).length > 0 && (
        <>
          <h4 style={kop}>Deelteksten</h4>
          <ol style={lijst}>
            {opzet.delen.map(d => {
              const scene = scenes.find(sc => sc.nummer === d.nummer);
              return (
                <li key={d.nummer}>{d.titel || `Deeltekst ${d.nummer}`} <span className="text-muted">({d.plotstap})</span>
                  {scene && <span className="text-muted"> — {scene.vorm}{scene.moment ? `: ${scene.moment}` : ''}</span>}
                </li>
              );
            })}
          </ol>
        </>
      )}

      {(opzet.bouwstenen || []).length > 0 && (
        <>
          <h4 style={kop}>Bouwstenen</h4>
          <ul style={{ ...lijst, listStyle: 'none', marginLeft: 0 }}>
            {opzet.bouwstenen.map(b => <li key={b.code}><code>[{b.code}]</code> {b.inhoud}</li>)}
          </ul>
        </>
      )}

      {(opzet.reconstructie || []).length > 0 && (
        <>
          <h4 style={kop}>Modelreconstructie in {opzet.reconstructie.length} zinnen (voor de leerkracht)</h4>
          <ol style={lijst}>
            {opzet.reconstructie.map((r, i) => (
              <li key={i}>{r.zin} {r.bronnen?.length > 0 && <span className="text-muted">[{r.bronnen.join(' + ')}]</span>}</li>
            ))}
          </ol>
        </>
      )}
      {parallel.length > 0 && (
        <div data-testid="parallel">
          <h4 style={kop}>Vergelijking met {opzet.voorbeeld?.titel ? `“${opzet.voorbeeld.titel}”` : 'het voorbeeld'}</h4>
          <table style={{ fontSize: 13 }}>
            <thead><tr><th>Bouwsteen</th><th>In het voorbeeld</th><th>In dit verhaal</th><th>Wat anders is, en waarom</th></tr></thead>
            <tbody>
              {parallel.map((p, i) => (
                <tr key={i}>
                  <td>{p.bouwsteen ? <code>[{p.bouwsteen}]</code> : <span className="text-muted">ander verschil</span>}</td>
                  <td>{p.voorbeeld}</td><td>{p.hier}</td><td>{p.verschil}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {opzet.variant_vier && <p className="mt-8"><strong>Groepje van vier:</strong> {opzet.variant_vier}</p>}
      {opzet.eindvraag && <p className="mt-8"><strong>Causale eindvraag:</strong> {opzet.eindvraag}</p>}

      <h4 style={kop}>{vanNu ? 'Verantwoording' : 'Historische verantwoording'}</h4>
      <Verantwoording titel={vanNu ? 'Berust op de werkelijkheid' : 'Historisch'} punten={verantwoording.historisch} />
      <Verantwoording titel="Verzonnen" punten={verantwoording.verzonnen} />
      <Verantwoording titel="Nakijken door de redactie" punten={verantwoording.nakijken} nadruk />
      {toonNakijkronde && <NakijkPunten punten={opzet.nakijkronde} />}
      {(opzet.bronnen || []).length > 0 && (
        <>
          <p className="mt-8"><strong>Bronnen bij het schrijven:</strong></p>
          <ul style={lijst}>
            {opzet.bronnen.map(b => <li key={b}><a href={b} target="_blank" rel="noopener noreferrer">{b}</a></li>)}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Wat de nakijkronde vond ("Laat de geschiedenis nakijken"): per punt de
 * deeltekst, het citaat, wat er niet klopt, een voorstel en hoe zeker het
 * model is. Een citaat dat niet in de tekst terug te vinden was, zegt dat.
 */
export function NakijkPunten({ punten }) {
  if (!punten || punten.length === 0) return null;
  return (
    <div style={{ background: 'var(--waarschuw-vlak)', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 14 }}
      data-testid="nakijkronde">
      <strong>Nakijkronde (taalmodel; de redactie beslist):</strong>
      <ul style={lijst}>
        {punten.map((p, i) => (
          <li key={i}>
            <span className="text-muted">{p.deeltekst ? `Deeltekst ${p.deeltekst}` : 'Het verhaal'} · {p.zekerheid}</span>
            {p.citaat && <> — <em>“{p.citaat}”</em></>}
            {p.gevonden === false && <span className="text-muted"> (niet letterlijk teruggevonden)</span>}
            <br />{p.probleem}
            {p.voorstel && <><br /><span className="text-muted">Voorstel:</span> {p.voorstel}</>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Verantwoording({ titel, punten, nadruk }) {
  if (!punten || punten.length === 0) return null;
  return (
    <div style={nadruk ? { background: 'var(--waarschuw-vlak)', borderRadius: 6, padding: '6px 10px', marginBottom: 8 } : { marginBottom: 8 }}>
      <strong>{titel}:</strong>
      <ul style={lijst}>{punten.map((p, i) => <li key={i}>{p}</li>)}</ul>
    </div>
  );
}

const MARKERING = /(\[\d[AB]\])/;

/**
 * Een alinea met de markeringen van de bouwstenen zichtbaar gemaakt (voor de
 * leerkracht, in het voorbeeld van de tekst of de les).
 */
export function GemarkeerdeTekst({ tekst }) {
  return (
    <>
      {(tekst || '').split(MARKERING).map((stuk, i) => (MARKERING.test(stuk)
        ? <code key={i} style={{ background: 'var(--waarschuw-vlak)', color: 'var(--waarschuw-tekst)', borderRadius: 3,
          padding: '0 3px', marginRight: 2, fontSize: '0.85em' }}>{stuk}</code>
        : <React.Fragment key={i}>{stuk}</React.Fragment>))}
    </>
  );
}

/** Of er in deze alinea's markeringen zijn om te tonen. */
export const heeftMarkeringen = (alineas) => (alineas || []).some(p => p.gemarkeerd);
